import { AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { SchedulerAttributes } from '@pccr-ifc/pccr-ifc';
import { useTranslation } from 'react-i18next';

import { PickSomeRestPartial } from '../../../shared/utils/types';
import { Job, JobUpsert } from '../../models/job';
import { useJobService } from '../services/use-job-service';
import {
  onCreateResourceMutationSuccess,
  onDeleteResourceMutationSuccess,
  onUpdateResourceMutationSuccess,
} from '../../../shared/utils/mutation';
import { displayErrors } from '../../../shared/utils/error';

export function useCreateJobMutation() {
  const jobService = useJobService();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<SchedulerAttributes, unknown, Omit<JobUpsert, 'id'>>(
    job => {
      const request = { schedule: job } as { schedule: Omit<SchedulerAttributes, 'id'> };
      return jobService.createJob(request);
    },
    {
      onSuccess(response) {
        onCreateResourceMutationSuccess(queryClient, 'JOB', response);
      },
      onError(error) {
        displayErrors(error, t);
      },
    }
  );
}

export function useUpdateJobMutation() {
  const jobService = useJobService();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<SchedulerAttributes, unknown, PickSomeRestPartial<JobUpsert, 'id'>>(
    job => jobService.updateJob(job),
    {
      onSuccess(response, vars) {
        onUpdateResourceMutationSuccess(queryClient, 'JOB', response, vars.id);
      },
      onError(error) {
        displayErrors(error, t);
      },
    }
  );
}

export function useDeleteJobMutation() {
  const jobService = useJobService();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<AxiosResponse<void>, unknown, Job['id']>(id => jobService.deleteJob(id), {
    onSuccess(_response, id) {
      onDeleteResourceMutationSuccess(queryClient, 'JOB', id);
    },
    onError(error) {
      displayErrors(error, t);
    },
  });
}
